import type {
  TipoRepresentacionGrafica,
  UrlsRepresentacionGraficaProps,
} from '../../../../base/interfaces/base'
import { IMPRESION_FORMATO, ImpresionFormatoProps } from '../interfaces/impresora.ts'
import { ES_SAFARI_ESCRITORIO } from './dispositivo.ts'

/** PDF a imprimir: Blob generado en el frontend o URL (representación gráfica del backend o blob:) */
export type PdfFuenteProps = Blob | string

/** Campos imprimibles de la representación gráfica del backend (`sin` y `xml` no se imprimen) */
export type CampoRepresentacionImpresion = keyof Pick<
  UrlsRepresentacionGraficaProps,
  'pdf' | 'rollo' | 'rollo2' | 'rolloResumen' | 'rolloReducido'
>

/**
 * URLs imprimibles de `UrlsRepresentacionGraficaProps`, todas opcionales: no todos los documentos
 * traen todos los formatos (solo las facturas tienen rollo2, resumen y reducido).
 */
export type RepresentacionGraficaUrls = {
  [K in CampoRepresentacionImpresion]?: UrlsRepresentacionGraficaProps[K] | null
}

/**
 * Única equivalencia entre el formato de impresión (lo que se guarda en la asignación) y el campo de
 * la representación gráfica del backend. Agregar un formato = una línea aquí.
 */
export const IMPRESION_FORMATO_REPRESENTACION = {
  [IMPRESION_FORMATO.PDF]: 'pdf',
  [IMPRESION_FORMATO.ROLLO]: 'rollo',
  [IMPRESION_FORMATO.ROLLO2]: 'rollo2',
  [IMPRESION_FORMATO.ROLLO_RESUMEN]: 'rolloResumen',
  [IMPRESION_FORMATO.RO_REDUCIDO]: 'rolloReducido',
} as const satisfies Record<ImpresionFormatoProps, CampoRepresentacionImpresion>

const FORMATOS = Object.keys(IMPRESION_FORMATO_REPRESENTACION) as ImpresionFormatoProps[]

/** Formatos de papel térmico (rollo) */
export const esFormatoRollo = (formato?: ImpresionFormatoProps | null) => formato !== IMPRESION_FORMATO.PDF

/** Formato equivalente a `user.tipoRepresentacionGrafica`, respaldo cuando no hay asignación */
export const formatoDesdeTipoRepresentacion = (
  tipo?: TipoRepresentacionGrafica | string | null,
): ImpresionFormatoProps =>
  // sin, xml o sin preferencia → rollo
  FORMATOS.find((f) => IMPRESION_FORMATO_REPRESENTACION[f] === tipo) ?? IMPRESION_FORMATO.ROLLO

/**
 * URL de la representación gráfica en el formato pedido, con respaldo al rollo (o al PDF).
 * Los formatos opcionales (rollo2, rolloResumen, rolloReducido) usan el rollo si no vienen.
 */
export const urlRepresentacionPorFormato = (
  rg: RepresentacionGraficaUrls | null | undefined,
  formato: ImpresionFormatoProps,
): string => {
  if (!rg) throw new Error('El documento no tiene representación gráfica')
  const url = rg[IMPRESION_FORMATO_REPRESENTACION[formato]] || rg.rollo || rg.pdf
  if (!url) throw new Error('El documento no tiene representación gráfica disponible')
  return url
}

/** Las URLs blob:/data: solo existen en este navegador; isi-print no puede descargarlas */
export const esUrlLocal = (url: string) => url.startsWith('blob:') || url.startsWith('data:')

/** Obtiene el PDF como Blob (descarga la URL si hace falta) */
export const pdfABlob = async (pdf: PdfFuenteProps): Promise<Blob> => {
  if (pdf instanceof Blob) return pdf
  const res = await fetch(pdf)
  if (!res.ok) throw new Error(`No se pudo obtener el PDF (${res.status})`)
  return res.blob()
}

/** Si la ventana nunca recupera el foco, el iframe de impresión se libera igual pasado este tiempo */
const IMPRESION_NAVEGADOR_MAX_MS = 10 * 60_000
/**
 * Sin visor de PDF integrado (ej. Chrome configurado para descargar los PDF) el iframe nunca carga:
 * pasado este tiempo se abre el PDF en otra pestaña. Dentro de los ~5 s del clic el navegador aún
 * permite abrir la pestaña sin bloquearla como ventana emergente.
 */
const IMPRESION_NAVEGADOR_CARGA_MS = 4_000

/**
 * Abre el diálogo de impresión del navegador / sistema operativo con el PDF, sin librerías: el PDF
 * se carga en un iframe oculto y se llama a `print()` en él. Si no se puede (CORS al descargarlo, o un
 * navegador que no permite imprimir el PDF desde el iframe), se abre el PDF en otra pestaña.
 *
 * El tamaño del papel lo define el propio PDF (rollo o carta) y la configuración de la impresora; el
 * navegador no puede cambiarlo. Para control real del rollo está isi-print.
 * Safari de escritorio no imprime bien un PDF dentro de un iframe: ahí se abre directo en otra
 * pestaña para imprimirlo desde su visor (⌘P).
 * @param formato Se conserva por compatibilidad; el navegador imprime el PDF tal como viene.
 */
export const imprimirEnNavegador = async (pdf: PdfFuenteProps, formato?: ImpresionFormatoProps | null) => {
  void formato
  if (ES_SAFARI_ESCRITORIO) {
    // Antes de cualquier await: Safari solo deja abrir la pestaña en el mismo instante del clic
    const url = pdf instanceof Blob ? URL.createObjectURL(pdf) : pdf
    window.open(url, '_blank', 'noopener,noreferrer')
    if (pdf instanceof Blob) setTimeout(() => URL.revokeObjectURL(url), 60_000)
    return
  }
  let blob: Blob
  try {
    blob = await pdfABlob(pdf)
  } catch {
    if (typeof pdf === 'string') window.open(pdf, '_blank', 'noopener,noreferrer')
    return
  }

  const url = URL.createObjectURL(blob)
  const iframe = document.createElement('iframe')
  iframe.setAttribute('aria-hidden', 'true')
  iframe.tabIndex = -1
  // Oculto pero renderizado: con display:none algunos navegadores imprimen una página en blanco
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden'

  let liberado = false
  const liberar = () => {
    if (liberado) return
    liberado = true
    window.removeEventListener('focus', alVolver)
    clearTimeout(limite)
    clearTimeout(sinCarga)
    iframe.remove()
    URL.revokeObjectURL(url)
  }
  // Al cerrar el diálogo de impresión la ventana recupera el foco: se espera un momento para no
  // cortar la impresión en curso y se libera el iframe
  const alVolver = () => setTimeout(liberar, 1_000)
  const limite = setTimeout(liberar, IMPRESION_NAVEGADOR_MAX_MS)
  const abrirEnPestana = () => {
    window.open(url, '_blank', 'noopener,noreferrer')
    // La pestaña ya tomó el PDF: se libera el iframe (y la URL) pasado un minuto
    setTimeout(liberar, 60_000)
  }
  const sinCarga = setTimeout(() => {
    iframe.onload = null
    abrirEnPestana()
  }, IMPRESION_NAVEGADOR_CARGA_MS)

  iframe.onload = () => {
    clearTimeout(sinCarga)
    try {
      const w = iframe.contentWindow
      if (!w) throw new Error('Sin ventana de impresión')
      w.focus()
      window.addEventListener('focus', alVolver, { once: true })
      // Algunos navegadores avisan el cierre del diálogo en el propio iframe
      w.addEventListener('afterprint', alVolver, { once: true })
      w.print()
    } catch {
      // El navegador no deja imprimir el PDF desde el iframe: se abre para imprimirlo desde su visor
      abrirEnPestana()
    }
  }
  iframe.src = url
  document.body.appendChild(iframe)
}

/** URL descargable desde fuera de este navegador (WhatsApp, correo, compartir) */
export const esUrlPublica = (u: unknown): u is string => typeof u === 'string' && /^https?:\/\//i.test(u)

/**
 * URL pública del documento: la del PDF actual si lo es; si es un PDF generado en el navegador, la
 * del formato carta (`obtenerPdf('PDF')`) cuando existe. null si solo existe en este navegador.
 */
export const obtenerUrlPublicaPdf = async (
  pdf: PdfFuenteProps | null,
  obtenerPdf?: (formato: ImpresionFormatoProps) => Promise<PdfFuenteProps> | PdfFuenteProps,
): Promise<string | null> => {
  if (esUrlPublica(pdf)) return pdf
  if (!obtenerPdf) return null
  try {
    const carta = await obtenerPdf(IMPRESION_FORMATO.PDF)
    return esUrlPublica(carta) ? carta : null
  } catch {
    return null
  }
}

const nombrePdf = (filename?: string) => {
  const base = (filename || 'documento').replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'documento'
  return base.toLowerCase().endsWith('.pdf') ? base : `${base}.pdf`
}

/**
 * El navegador permite compartir archivos (hoja de compartir del sistema en teléfonos: WhatsApp,
 * correo, AirPrint o apps de impresión de Android).
 */
export const puedeCompartirArchivos = (): boolean => {
  try {
    const prueba = new File([new Blob(['%PDF'], { type: 'application/pdf' })], 'prueba.pdf', {
      type: 'application/pdf',
    })
    return typeof navigator !== 'undefined' && !!navigator.canShare && navigator.canShare({ files: [prueba] })
  } catch {
    return false
  }
}

/**
 * Comparte el PDF con la hoja de compartir del sistema. Si no se puede descargar el archivo (CORS),
 * comparte el enlace. Devuelve false si el usuario canceló o el navegador no permite compartir.
 */
export const compartirPdf = async (
  pdf: PdfFuenteProps,
  titulo: string,
  filename?: string,
): Promise<boolean> => {
  if (typeof navigator === 'undefined' || !navigator.share) return false
  try {
    const blob = await pdfABlob(pdf)
    const file = new File([blob], nombrePdf(filename), { type: 'application/pdf' })
    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], title: titulo })
      return true
    }
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return false
    // No se pudo obtener el archivo: se intenta con el enlace
  }
  if (!esUrlPublica(pdf)) return false
  try {
    await navigator.share({ url: pdf, title: titulo })
    return true
  } catch {
    return false
  }
}

/** Descarga el PDF; si no se puede (CORS), lo abre en otra pestaña */
export const descargarPdf = async (pdf: PdfFuenteProps, filename?: string) => {
  let url: string
  let revocar = false
  try {
    url = URL.createObjectURL(await pdfABlob(pdf))
    revocar = true
  } catch {
    if (typeof pdf !== 'string') throw new Error('No se pudo descargar el documento')
    url = pdf
  }
  const a = document.createElement('a')
  a.href = url
  a.download = nombrePdf(filename)
  a.target = '_blank'
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  if (revocar) setTimeout(() => URL.revokeObjectURL(url), 60_000)
}
